<template>
  <div class="app-page chat-page">
    <div class="chat-wrapper">
      <!-- 会话列表区域 -->
      <div class="conversation-list" :class="{ 'collapsed': isMobile && !showConversation }">
        <div class="search-bar">
          <div class="search-input-wrapper">
            <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input
              type="text"
              placeholder="搜索会话..."
              v-model="searchKeyword"
              class="search-input"
            >
          </div>
        </div>

        <div class="conversations">
          <div
            v-for="conv in filteredConversations"
            :key="conv.id"
            class="conversation-item"
            :class="{ 'active': conv.id === activeConversationId }"
            @click="selectConversation(conv)"
          >
            <div class="conv-avatar-wrapper">
              <img :src="conv.targetAvatar || defaultAvatar" class="conv-avatar" alt="用户头像">
              <span class="online-dot" v-if="conv.online"></span>
            </div>
            <div class="conv-info">
              <div class="conv-header">
                <span class="nickname">
                  {{ conv.targetNickname }}
                  <span class="friend-tag" v-if="friendIds.has(conv.targetUserId)" title="好友">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#FF7E7E" stroke="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  </span>
                </span>
                <span class="time">{{ formatTime(conv.lastTime) }}</span>
              </div>
              <div class="last-message">
                <span>{{ conv.lastMessage }}</span>
                <span class="unread-badge" v-if="conv.unreadCount > 0">{{ conv.unreadCount > 99 ? '99+' : conv.unreadCount }}</span>
              </div>
            </div>
          </div>
          <div v-if="filteredConversations.length === 0" class="empty-conversations">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#B2BEC3" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            <p>暂无会话</p>
          </div>
        </div>
      </div>

      <!-- 聊天详情区域 -->
      <div class="chat-detail" v-if="activeConversationId || !isMobile">
        <!-- 聊天头部 -->
        <div class="chat-header">
          <button
            class="back-btn"
            @click="toggleConversation"
            v-if="isMobile && !showConversation"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <div v-if="activeConversation" class="chat-header-info">
            <div class="chat-header-avatar-wrapper">
              <img :src="activeConversation.targetAvatar || defaultAvatar" class="chat-header-avatar" alt="">
              <span class="online-dot large"></span>
            </div>
            <div class="chat-header-text">
              <span class="target-name">{{ activeConversation.targetNickname }}</span>
              <span class="target-status">在线</span>
            </div>
            <button
              v-if="!friendIds.has(activeConversation.targetUserId) && activeConversation.targetUserId > 0"
              class="add-friend-btn"
              @click="handleAddFriend"
              title="添加好友"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
              添加好友
            </button>
            <!-- 好友通话按钮 -->
            <div class="call-btns" v-if="friendIds.has(activeConversation.targetUserId)">
              <button class="call-btn" @click="handleVideoCall" title="视频通话">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
              </button>
              <button class="call-btn" @click="showProfileCard" title="个人资料">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.58-7 8-7s8 3 8 7"/></svg>
              </button>
            </div>
          </div>
        </div>

        <!-- 消息列表 -->
        <div
          class="message-list"
          ref="messageList"
          @scroll="handleScroll"
        >
          <div v-if="isLoadingMore" class="loading-more">
            <div class="loading-spinner"></div>
            <span>加载更多消息...</span>
          </div>

          <div
            v-for="msg in messageHistory"
            :key="msg.id"
            class="message-item"
            :class="{ 'sent': msg.senderId === currentUserId, 'received': msg.senderId !== currentUserId }"
          >
            <div class="msg-avatar-wrapper">
              <img
                :src="msg.senderId === currentUserId ? myAvatar : (activeConversation.targetAvatar || defaultAvatar)"
                class="msg-avatar"
                alt="发送者头像"
              >
            </div>
            <div class="msg-content">
              <div class="msg-text" v-if="msg.type === 1">{{ msg.content }}</div>

              <div class="msg-image" v-if="msg.type === 2">
                <img :src="msg.content" class="message-image" alt="图片消息" @click="previewImage(msg.content)">
              </div>

              <div class="msg-file" v-if="msg.type === 4">
                <a :href="msg.content" target="_blank" class="file-link">
                  <svg class="file-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  <span class="file-name">{{ msg.fileName }}</span>
                </a>
              </div>

              <!-- 语音消息 -->
              <div class="msg-voice" v-if="msg.type === 7" @click="toggleVoicePlay(msg)">
                <div class="voice-player" :class="{ playing: msg._playing }">
                  <svg class="voice-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                  <div class="voice-wave">
                    <span v-for="i in 4" :key="i" class="wave-bar" :class="{ active: msg._playing }"></span>
                  </div>
                  <span class="voice-duration">{{ msg.duration || 0 }}''</span>
                </div>
              </div>

              <div class="msg-file" v-if="msg.type ===6">
                <a class="file-link gift-link">
                  <svg class="file-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF7E7E" stroke-width="2"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
                  <span class="file-name">{{ msg.content }}</span>
                </a>
              </div>

              <!-- 礼物请求消息 -->
              <div
                class="msg-gift-request"
                v-if="msg.type === 5"
                @click="handleGiftMessageClick(msg)"
                :class="{ 'clickable': isGiftUnprocessed(msg) }"
              >
                <div class="gift-request-content">
                  <svg class="gift-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF7E7E" stroke-width="2"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
                  <span v-if="msg.senderId === currentUserId">
                    您向对方赠送了礼物：{{ msg.productName }}
                  </span>
                  <span v-else>
                    您收到对方赠送的礼物：{{ msg.productName }}
                  </span>
                </div>
                <div class="gift-request-status" v-if="!isGiftUnprocessed(msg)">
                  <span v-if="isGiftAccepted(msg)" class="accepted">已接受</span>
                  <span v-if="isGiftRejected(msg)" class="rejected">已拒绝</span>
                </div>
              </div>

              <!-- 视频通话记录 -->
              <div v-if="msg.type === 10" class="msg-call-record">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" stroke-width="2">
                  <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
                <span>{{ msg.senderId === currentUserId ? '您发起了视频通话' : '对方发起了视频通话' }}</span>
              </div>

              <!-- 游戏邀请 -->
              <div
                v-if="msg.type === 3"
                class="msg-game-invite"
                :class="{ expired: isGameInviteExpired(msg), clickable: msg.receiverId === currentUserId }"
                @click="handleGameInviteClick(msg)"
              >
                <div class="game-invite-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="2" y="6" width="20" height="12" rx="2"/>
                    <circle cx="8" cy="12" r="1.5" fill="currentColor"/>
                    <circle cx="16" cy="12" r="1.5" fill="currentColor"/>
                  </svg>
                </div>
                <div class="game-invite-body">
                  <div class="game-invite-title" v-if="msg.senderId === currentUserId">
                    你邀请了好友 <strong>{{ parseGameInvite(msg).inviterName }}</strong> 玩 <strong>{{ parseGameInvite(msg).gameTypeName }}</strong>
                  </div>
                  <div class="game-invite-title" v-else>
                    好友 <strong>{{ parseGameInvite(msg).inviterName }}</strong> 邀请你玩 <strong>{{ parseGameInvite(msg).gameTypeName }}</strong>
                  </div>
                  <div class="game-invite-status" v-if="isGameInviteExpired(msg)">⏰ 邀请已过期</div>
                  <div class="game-invite-status" v-else-if="msg.receiverId === currentUserId">🎯 点击接受邀请，一起游戏！</div>
                  <div class="game-invite-status" v-else>⏳ 等待对方接受邀请...</div>
                </div>
              </div>

              <div class="msg-time">{{ formatTime(msg.sendTime) }}</div>
            </div>
          </div>
        </div>

        <button
          class="scroll-to-bottom-btn"
          @click="scrollToBottom"
          v-if="showScrollToBottomBtn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
        </button>

        <div class="input-area" v-if="activeConversation">
          <label class="file-upload-btn" for="file-upload" title="上传文件">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
            <input
              id="file-upload"
              type="file"
              accept="image/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              @change="handleFileSelect"
              hidden
            >
          </label>

          <!-- 语音录制按钮 -->
          <button v-if="!isRecording" class="voice-btn" title="语音消息" @click="startRecording">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
          </button>

          <!-- 录制中 -->
          <div v-if="isRecording" class="recording-indicator">
            <span class="recording-dot"></span>
            <span class="recording-timer">{{ recordingTimer }}</span>
            <button class="stop-recording-btn" @click="stopRecording">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
            </button>
          </div>

          <div v-if="selectedFile" class="file-preview">
            <img
              v-if="isImageFile(selectedFile)"
              :src="filePreviewUrl"
              class="image-preview"
              alt="预览图片"
            >
            <div v-else class="file-info">
              <svg class="file-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <span class="file-name">{{ selectedFile.name }}</span>
              <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
            </div>
            <button class="remove-file" @click="clearSelectedFile">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <textarea
            v-model="inputContent"
            placeholder="输入消息..."
            class="message-input"
            @keydown.enter.exact="sendMessage"
            @keydown.enter.shift="handleShiftEnter"
            rows="1"
          ></textarea>

          <!-- AI帮聊按钮 -->
          <el-popover
            v-model:visible="aiPopoverVisible"
            placement="top-end"
            width="280"
            trigger="click"
          >
            <div v-if="aiLoading" class="ai-loading">
              <div class="ai-loading-spinner"></div>
              <span>AI 正在生成回复...</span>
            </div>
            <div v-else class="ai-reply-list">
              <div
                v-for="(item, index) in aiReplyList"
                :key="index"
                class="ai-reply-item"
                @click="sendAiMessage(item)"
              >
                {{ item }}
              </div>
            </div>
            <div v-if="aiError" class="ai-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f56c6c" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {{ aiError }}
            </div>
            <template #reference>
              <button class="ai-btn" title="AI帮聊">
                <el-icon :size="18"><MagicStick /></el-icon>
              </button>
            </template>
          </el-popover>

          <button class="send-btn" @click="sendMessage" :disabled="!inputContent.trim() && !selectedFile">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
      <!-- 用户资料卡片 -->
      <UserProfileCard
        :visible="profileCardVisible"
        :profile="targetProfile"
        title="个人资料"
        @close="profileCardVisible = false"
      />
      </div>
    </div>
  </div>

</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick, reactive } from 'vue';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import UserProfileCard from '@/views/match/UserProfileCard.vue';
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import { MagicStick } from '@element-plus/icons-vue';
import socketService from '@/api/socket';
import giftApi from '@/api/gift';
import userProfileApi from '@/api/userProfile';
import friendApi from '@/api/friend';
import gameApi from '@/api/game';


// 路由实例
const route = useRoute();
const router = useRouter();
const hasSentGreeting = ref({});

// 消息列表DOM引用
const messageList = ref(null);

// 用户信息
const storedUserId = localStorage.getItem('userId');
const currentUserId = ref(storedUserId ? Number(storedUserId) : null);
const store = useStore();
const myAvatar = ref('');
const defaultAvatar = 'https://picsum.photos/id/1005/200';

const fetchMyAvatar = async () => {
  const user = store.state.user;
  if (user?.avatar) {
    myAvatar.value = user.avatar;
    return;
  }
  try {
    const userData = await store.dispatch('fetchUserInfo');
    myAvatar.value = userData?.avatar || defaultAvatar;
  } catch {
    myAvatar.value = defaultAvatar;
  }
};

function handleVideoCall() {
  if (!activeConversation.value) return
  // 生成唯一房间号
  const roomId = `dating_${Date.now()}_${currentUserId.value}`
  const myName = localStorage.getItem('username') || '用户'
  const targetName = activeConversation.value.targetNickname

  // 先发通知让对方知道房间号（用call_invite类型，后端直接转发）
  socketService.send({
    type: 'call_invite',
    senderId: currentUserId.value,
    targetUserId: activeConversation.value.targetUserId,
    roomId: roomId,
    nickname: localStorage.getItem('username') || '用户'
  }).catch(console.error)

  // 在聊天记录中插入一条视频通话记录
  const callMsg = {
    senderId: currentUserId.value,
    receiverId: activeConversation.value.targetUserId,
    content: '[视频通话]',
    type: 10,
    sendTime: new Date().toISOString()
  }
  // 发起方立即显示，接收方通过 handleReceivedMessage 显示
  messageHistory.value.push({ ...callMsg, id: Date.now() })
  scrollToBottom()
  // 通过WebSocket持久化并通知对方
  socketService.send(callMsg).catch(console.error)

  // 打开 MiroTalk 房间
  window.open(
    `https://shiguangtongpin.cn:8443/join/${roomId}?name=${encodeURIComponent(myName)}`,
    '_blank',
    'width=1024,height=768'
  )
}

// ========== 用户资料卡片 ==========
const profileCardVisible = ref(false);
const targetProfile = ref(null);

const showProfileCard = async () => {
  if (!activeConversation.value) return;
  profileCardVisible.value = true;
  targetProfile.value = null;
  try {
    // 获取用户基本信息和头像
    const basicInfo = {
      nickname: activeConversation.value.targetNickname,
      avatar: activeConversation.value.targetAvatar || defaultAvatar,
      userId: activeConversation.value.targetUserId
    };
    // 获取详细资料
    const res = await userProfileApi.getByUserId(activeConversation.value.targetUserId);
    console.log('📋 原始API响应:', res.data);
    if (res.data && (res.data.code === '200' || res.data.code === 200) && res.data.data) {
      targetProfile.value = { ...basicInfo, ...res.data.data };
    } else {
      console.warn('⚠️ 获取资料失败，仅使用基础信息', res.data);
      targetProfile.value = basicInfo;
    }
  } catch (err) {
    console.error('获取用户资料失败:', err);
    targetProfile.value = { nickname: activeConversation.value.targetNickname, avatar: defaultAvatar };
  }
};

// 会话状态
const conversations = ref([]);
const activeConversationId = ref(null);
const activeConversation = ref(null);
const searchKeyword = ref('');

// 消息状态
const messageHistory = ref([]);
const inputContent = ref('');
const pageNum = ref(1);
const pageSize = ref(20);
const hasMore = ref(true);
const isLoadingMore = ref(false);
const showScrollToBottomBtn = ref(false);
const initialLoadCompleted = ref(false);

// 礼物请求状态缓存
const giftRequests = ref([]);

// 好友关系
const friendIds = reactive(new Set());

// 响应式布局
const isMobile = ref(window.innerWidth < 768);
const showConversation = ref(true);

// 文件相关
const selectedFile = ref(null);
const filePreviewUrl = ref('');
const uploadProgress = ref(0);

// 语音录制相关
const isRecording = ref(false);
const recordingTimer = ref('0:00');
let mediaRecorder = null;
let audioChunks = [];
let recordingStartTime = null;
let recordingInterval = null;

// 语音播放相关
const audioPlayer = ref(new Audio());


// 语音录制
const startRecording = async () => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      ElMessage.error('您的浏览器不支持语音录制')
      return
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    audioChunks = []
    mediaRecorder = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
    })

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data)
      }
    }

    mediaRecorder.onstop = () => {
      // 停止所有轨道
      stream.getTracks().forEach(track => track.stop())
      clearInterval(recordingInterval)

      const audioBlob = new Blob(audioChunks, { type: mediaRecorder.mimeType })
      if (audioBlob.size > 0) {
        sendVoiceMessage(audioBlob)
      }
    }

    mediaRecorder.start(100) // 每100ms收集一次数据
    isRecording.value = true
    recordingStartTime = Date.now()
    recordingTimer.value = '0:00'

    recordingInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - recordingStartTime) / 1000)
      const mins = Math.floor(elapsed / 60)
      const secs = elapsed % 60
      recordingTimer.value = `${mins}:${secs.toString().padStart(2, '0')}`
      // 限制最长60秒
      if (elapsed >= 60) {
        stopRecording()
        ElMessage.info('语音录制已达60秒上限')
      }
    }, 1000)
  } catch (error) {
    console.error('启动录音失败:', error)
    ElMessage.error('无法访问麦克风，请检查权限设置')
  }
}

const stopRecording = () => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop()
    isRecording.value = false
  }
}

// 发送语音消息
const sendVoiceMessage = async (audioBlob) => {
  if (!socketService.isConnected || !activeConversation.value) {
    ElMessage.warning('WebSocket未连接')
    return
  }

  try {
    const formData = new FormData()
    const audioFile = new File([audioBlob], `voice_${Date.now()}.webm`, { type: audioBlob.type })
    formData.append('file', audioFile)
    formData.append('userId', currentUserId.value)
    formData.append('fileType', '7') // 语音消息类型

    const res = await axios.post('/message/api/v1/files/upload', formData)
    if (!res.data.success || !res.data.data) {
      ElMessage.error('语音上传失败')
      return
    }

    const fileData = res.data.data
    const duration = Math.floor(((Date.now() - recordingStartTime) / 1000))

    const message = {
      senderId: currentUserId.value,
      receiverId: activeConversation.value.targetUserId,
      content: fileData.accessUrl || fileData.fileUrl,
      type: 7,
      fileName: audioFile.name,
      duration: duration,
      fileUrl: fileData.accessUrl || fileData.fileUrl,
      sendTime: new Date().toISOString()
    }

    socketService.send(message).then(() => {
      messageHistory.value.push(message)
      scrollToBottom()
      fetchConversations()
    }).catch(error => {
      console.error('发送语音消息失败:', error)
      ElMessage.error('发送语音消息失败')
    })
  } catch (error) {
    console.error('语音上传失败:', error)
    ElMessage.error('语音上传失败')
  }
}

// 语音播放
const voicePlayingUrl = ref(null);

const toggleVoicePlay = (msg) => {
  const audio = audioPlayer.value

  // 如果正在播放该消息，则停止
  if (voicePlayingUrl.value === msg.content) {
    audio.pause()
    audio.currentTime = 0
    msg._playing = false
    voicePlayingUrl.value = null
    return
  }

  // 停止当前播放
  if (voicePlayingUrl.value !== null) {
    const prevMsg = messageHistory.value.find(m => m.content === voicePlayingUrl.value)
    if (prevMsg) prevMsg._playing = false
    audio.pause()
  }

  // 播放新语音
  audio.src = msg.content
  audio.play().catch(() => {
    ElMessage.error('语音播放失败')
    msg._playing = false
    voicePlayingUrl.value = null
    return
  })
  msg._playing = true
  voicePlayingUrl.value = msg.content

  audio.onended = () => {
    msg._playing = false
    voicePlayingUrl.value = null
  }

  audio.onerror = () => {
    msg._playing = false
    voicePlayingUrl.value = null
    ElMessage.error('语音播放失败')
  }
}

// 初始化WebSocket
const initWebSocket = () => {
  socketService.off('open');
  socketService.off('message');
  socketService.off('error');
  socketService.off('maxReconnect');

  socketService.on('open', () => {
    console.log('WebSocket连接已建立');
    if (currentUserId.value) {
      socketService.send({
        type: "connect",
        userId: currentUserId.value
      }).catch(error => {
        console.error('发送连接信息失败:', error);
      });

      checkRouteForGreeting();
    }
  });

  socketService.on('message', handleReceivedMessage);

  // 处理视频通话邀请
  socketService.on('message', (msg) => {
    if (msg && msg.type === 'call_invite' && msg.senderId !== currentUserId.value) {
      const myName = localStorage.getItem('username') || '用户'
      ElMessageBox.confirm(
        `${msg.nickname || '对方'} 邀请您视频通话`,
        '视频通话',
        { confirmButtonText: '加入通话', cancelButtonText: '取消', type: 'info' }
      ).then(() => {
        window.open(
          `https://shiguangtongpin.cn:8443/join/${msg.roomId}?name=${encodeURIComponent(myName)}`,
          '_blank',
          'width=1024,height=768'
        )
      }).catch(() => {})
    }
  })

  socketService.on('error', (error) => {
    console.error('WebSocket错误:', error);
  });

  socketService.on('maxReconnect', (maxAttempts) => {
    console.error(`已达到最大${maxAttempts}次重连尝试，请检查服务是否可用`);
    ElMessage.error(`已达到最大${maxAttempts}次重连尝试，请检查服务是否可用`);
  });

  if (currentUserId.value) {
    socketService.connect({ userId: currentUserId.value });
  } else {
    console.log('等待用户ID可用后再连接WebSocket');
  }
};

// 获取礼物请求列表并缓存
const fetchGiftRequests = async () => {
  if (!currentUserId.value) return;

  try {
    const response = await giftApi.list({
      userId: currentUserId.value
    });

    if (response.data.code==200) {
      giftRequests.value = response.data.data;
      console.log('礼物请求状态已更新:', giftRequests.value);
    }
  } catch (error) {
    console.error('获取礼物请求列表失败:', error);
  }
};

// 检查路由参数是否需要发送问候
const checkRouteForGreeting = async () => {
  const targetUserId = route.query.targetUserId;
  if (targetUserId && socketService.isConnected) {
    const conversationExists = await checkConversationExists(Number(targetUserId));
    if (!conversationExists && !hasSentGreeting.value[targetUserId]) {
      sendGreeting(Number(targetUserId));
      hasSentGreeting.value[targetUserId] = true;
    }
  }
};

// 检查会话是否存在
const checkConversationExists = async (targetUserId) => {
  try {
    const res = await axios.get('/message/api/v1/messages/conversations', {
      params: { userId: currentUserId.value }
    });
    return res.data.success && res.data.data.some(
      conv => conv.targetUserId === Number(targetUserId)
    );
  } catch (error) {
    console.error('检查会话存在失败:', error);
    return false;
  }
};

// 发送问候语
const sendGreeting = (targetUserId) => {
  const greetingMsg = {
    senderId: currentUserId.value,
    receiverId: targetUserId,
    content: '你好',
    type: 1,
    sendTime: new Date().toISOString()
  }

  socketService.send(greetingMsg)
    .then(() => {
      messageHistory.value.push(greetingMsg);
      scrollToBottom();
      setTimeout(() => {
        fetchConversations();
      }, 1000);
    })
    .catch(error => {
      console.error('发送问候消息失败:', error);
    });
};

// 处理接收消息
const handleReceivedMessage = (message) => {
  if (message.type === 10) {
    // 视频通话记录，直接显示
    if (message.receiverId === currentUserId.value) {
      messageHistory.value.push(message);
      scrollToBottom();
    }
    return;
  }
  if (message.type == 5) {
    // 如果是视频通话邀请
    if (message.roomId) {
      messageHistory.value.push({
        ...message,
        processed: message.processed || false,
        _isCallInvite: true
      });
    } else {
      messageHistory.value.push({
        ...message,
        processed: message.processed || false
      });
    }

    if (message.receiverId === currentUserId.value) {
      // 如果是视频通话邀请，提示加入
      if (message.roomId) {
        ElMessageBox.confirm(
          `${message.senderNickname || '对方'} 邀请您视频通话`,
          '视频通话',
          { confirmButtonText: '加入通话', cancelButtonText: '取消', type: 'info' }
        ).then(() => {
          const myName = localStorage.getItem('username') || '用户'
          window.open(
            `https://shiguangtongpin.cn/call/join/${message.roomId}?name=${encodeURIComponent(myName)}`,
            '_blank',
            'width=1024,height=768'
          )
        }).catch(() => {})
      }
      scrollToBottom();
      markAsRead(currentUserId.value, message.senderId);
    }
    return;
  }

  if (message.receiverId === currentUserId.value && message.senderId === activeConversation.value?.targetUserId) {
    messageHistory.value.push(message);
    if (messageList.value && messageList.value.scrollTop + messageList.value.clientHeight < messageList.value.scrollHeight - 100) {
      showScrollToBottomBtn.value = true;
    } else {
      scrollToBottom();
    }
    markAsRead(currentUserId.value, message.senderId);
  }
};

// 处理礼物消息点击事件
const handleGiftMessageClick = (message) => {
  if (message.receiverId === currentUserId.value && isGiftUnprocessed(message)) {
    handleGiftRequestMessage(message);
  }
};

// 处理礼物请求消息
const handleGiftRequestMessage = async (message) => {
  try {
    const result = await ElMessageBox.confirm(
      `用户 ${message.senderNickname || '对方'} 向您赠送了商品《${message.productName}》，是否接受？`,
      '收到礼物请求',
      {
        confirmButtonText: '接受',
        cancelButtonText: '拒绝',
        type: 'info',
        center: true,
        distinguishCancelAndClose: true
      }
    );
    console.log(result)

    await giftApi.handleGiftRequest({
      requestId: Number(message.requestId),
      receiverUserId: currentUserId.value,
      status: 1
    });

    await fetchGiftRequests();

    if (socketService.isConnected) {
      await socketService.send({
        type: 6,
        requestId: message.requestId,
        senderId: currentUserId.value,
        receiverId: message.senderId,
        productId: message.productId,
        accepted: true,
        sendTime: new Date().toISOString(),
        content:'接受礼物'
      });
        scrollToBottom();
        fetchConversations();
    }

    ElMessage.success('您已接受该礼物');
  } catch (error) {
    if (error === 'cancel') {
      try {
        await giftApi.handleGiftRequest({
          requestId: Number(message.requestId),
          receiverUserId: currentUserId.value,
          status: 2
        });

        await fetchGiftRequests();

        if (socketService.isConnected) {
          await socketService.send({
            type: 6,
            requestId: message.requestId,
            senderId: currentUserId.value,
            receiverId: message.senderId,
            productId: message.productId,
            accepted: false,
            sendTime: new Date().toISOString(),
            content:'拒绝礼物'
          });
        scrollToBottom();
        fetchConversations();
        }

        ElMessage.info('您已拒绝该礼物');
      } catch (rejectError) {
        console.error('处理礼物拒绝失败:', rejectError);
        ElMessage.error('处理礼物请求失败，请稍后重试');
      }
      return;
    }

    if (error === 'close') {
      console.log('用户关闭了礼物请求弹窗，不执行任何操作');
      return;
    }

    console.error('处理礼物请求时发生未知错误:', error);
    ElMessage.error('处理礼物请求失败，请稍后重试');
  }
};


// 判断礼物是否未处理（状态为0）
const isGiftUnprocessed = (message) => {
  const giftRequest = giftRequests.value.find(item => item.id === Number(message.requestId));
  return !giftRequest || giftRequest.status === 0;
};

// 判断礼物是否已接受（状态为1）
const isGiftAccepted = (message) => {
  const giftRequest = giftRequests.value.find(item => item.id === Number(message.requestId));
  return giftRequest && giftRequest.status === 1;
};

// 判断礼物是否已拒绝（状态为2）
const isGiftRejected = (message) => {
  const giftRequest = giftRequests.value.find(item => item.id === Number(message.requestId));
  return giftRequest && giftRequest.status === 2;
};

// 处理文件选择
const handleFileSelect = (e) => {
  const file = e.target.files[0];
  if (file) {
    selectedFile.value = file;

    if (isImageFile(file)) {
      const reader = new FileReader();
      reader.onload = (event) => {
        filePreviewUrl.value = event.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      filePreviewUrl.value = '';
    }
  }
};

// 判断图片文件
const isImageFile = (file) => {
  return file && file.type.startsWith('image/');
};

// 格式化文件大小
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 清除选中文件
const clearSelectedFile = () => {
  selectedFile.value = null;
  filePreviewUrl.value = '';
  document.getElementById('file-upload').value = '';
};

// 上传文件
const uploadFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', currentUserId.value);
    formData.append('fileType', isImageFile(file) ? '2' : '4');

    const res = await axios.post('/message/api/v1/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        uploadProgress.value = Math.round((progressEvent.loaded / progressEvent.total) * 100);
      }
    });

    return res.data.success ? res.data.data : null;
  } catch (error) {
    console.error('文件上传错误:', error);
    return null;
  }
};

// 图片预览
const previewImage = (url) => {
  window.open(url, '_blank');
};

// 初始化
onMounted(() => {
  if (!currentUserId.value) {
    const handleStorageChange = () => {
      const newUserId = localStorage.getItem('userId');
      if (newUserId) {
        currentUserId.value = Number(newUserId);
        initWebSocket();
        fetchGiftRequests();
        fetchFriendStatus();
        window.removeEventListener('storage', handleStorageChange);
      }
    };

    window.addEventListener('storage', handleStorageChange);
  } else {
    initWebSocket();
    fetchGiftRequests();
  }

  fetchConversations();
  fetchFriendStatus();
  fetchMyAvatar();
  window.addEventListener('resize', handleResize);
  window.addEventListener('popstate', handlePopState);
  initialLoadCompleted.value = false;

  // 强制滚动到最底部（多次尝试确保渲染完成）
  const forceScrollToBottom = () => {
    scrollToBottom(false);
    [50, 150, 300, 600].forEach(delay => {
      setTimeout(() => scrollToBottom(false), delay);
    });
  };

  watch(messageHistory, (newVal, oldVal) => {
    if (oldVal.length === 0 && newVal.length > 0) {
      // 首次加载：强制滚动到底部，多次重试
      forceScrollToBottom();
      setTimeout(() => {
        initialLoadCompleted.value = true;
      }, 800);
    }
    else if (newVal.length > oldVal.length) {
      // 新消息：如果已经在底部则平滑滚动到底部
      if (messageList.value && messageList.value.scrollTop + messageList.value.clientHeight >= messageList.value.scrollHeight - 50) {
        scrollToBottom(true);
      } else {
        showScrollToBottomBtn.value = true;
      }
    }
  });

  watch(
    () => route.query.targetUserId,
    async (newTargetId) => {
      if (newTargetId && socketService.isConnected) {
        await checkRouteForGreeting();
      }
    }
  );
});

// 清理
onUnmounted(() => {
  socketService.off('message', handleReceivedMessage);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('popstate', handlePopState);
});

// 窗口大小变化
const handleResize = () => {
  isMobile.value = window.innerWidth < 768;
  if (!isMobile.value) showConversation.value = true;
};

// 手机端返回键拦截：在聊天详情时回退到会话列表
const handlePopState = () => {
  if (isMobile.value && activeConversationId.value && !showConversation.value) {
    showConversation.value = true;
  }
};

// 过滤会话
const filteredConversations = computed(() => {
  if (!searchKeyword.value) return conversations.value;
  const keyword = searchKeyword.value.toLowerCase();
  return conversations.value.filter(conv =>
    (conv.targetNickname || '').toLowerCase().includes(keyword) ||
    (conv.lastMessage || '').toLowerCase().includes(keyword)
  );
});

// 获取会话列表
const fetchConversations = async () => {
  try {
    const res = await axios.get('/message/api/v1/messages/conversations', {
      params: { userId: currentUserId.value }
    });
    if (res.data.success) {
      conversations.value = res.data.data;
      const targetUserId = route.query.targetUserId;
      if (targetUserId) {
        const targetConv = conversations.value.find(
          conv => conv.targetUserId === Number(targetUserId)
        );
        if (targetConv) {
          selectConversation(targetConv);
          return;
        } else {
          if (currentUserId.value && Number(targetUserId) !== currentUserId.value) {
            const tempConv = {
              id: `temp-${targetUserId}`,
              targetUserId: Number(targetUserId),
              targetNickname: '新聊天',
              targetAvatar: defaultAvatar,
              lastMessage: '',
              lastTime: new Date().toISOString(),
              unreadCount: 0
            };
            selectConversation(tempConv);
          }
        }
      }
      if (conversations.value.length > 0 && !activeConversationId.value && !isMobile.value) {
        selectConversation(conversations.value[0]);
      }
    }
  } catch (error) {
    console.error('获取会话列表失败:', error);
  }
};

// 获取好友列表，构建好友ID集合
const fetchFriendStatus = async () => {
  if (!currentUserId.value) return;
  try {
    const res = await friendApi.getFriendList(currentUserId.value);
    if (res.data.success && res.data.data) {
      friendIds.clear();
      res.data.data.forEach(f => friendIds.add(f.friendId));
    }
  } catch (error) {
    console.error('获取好友列表失败:', error);
  }
};

// 添加好友
const handleAddFriend = async () => {
  if (!activeConversation.value) return;
  try {
    const res = await friendApi.sendApply(currentUserId.value, activeConversation.value.targetUserId, '');
    if (res.data && res.data.success) {
      ElMessage.success('好友申请已发送');
    } else {
      ElMessage.error(res.data?.msg || '发送好友申请失败');
    }
  } catch (error) {
    console.error('发送好友申请失败:', error);
    ElMessage.error('发送好友申请失败');
  }
};

// 获取消息历史
const fetchMessageHistory = async (userId, targetUserId, reset = false) => {
  if (reset) {
    pageNum.value = 1;
    messageHistory.value = [];
    hasMore.value = true;
    initialLoadCompleted.value = false;
    isLoadingMore.value = false;
  }

  if (!hasMore.value || isLoadingMore.value || (reset === false && !initialLoadCompleted.value)) {
    return;
  }

  isLoadingMore.value = true;
  
  const oldScrollHeight = messageList.value?.scrollHeight || 0;

  try {
    // 后端 Page 1 = 最新消息，内部已按 sendTime 升序排列（最旧在上，最新在下）
    if (reset) {
      pageNum.value = 1;
    } else {
      // 加载更早的消息：翻到下一页
      pageNum.value++;
    }

    const res = await axios.get('/message/api/v1/messages/history', {
      params: { userId, targetUserId, pageNum: pageNum.value, pageSize: pageSize.value }
    });

    if (res.data.success) {
      const { records, total } = res.data.data;
      const totalPages = Math.ceil(total / pageSize.value);

      // 首次加载直接替换，加载更多则追加到前面（更早的消息）
      messageHistory.value = reset ? records : [...records, ...messageHistory.value];
      // 还有更早的消息吗？当前页码 < 总页数 => 还有
      hasMore.value = pageNum.value < totalPages;

      // 加载更多时保持滚动位置：新消息被prepend后，scrollTop需要调整以保持相同视觉位置
      if (!reset && records.length > 0 && messageList.value) {
        nextTick(() => {
          const newScrollHeight = messageList.value.scrollHeight;
          messageList.value.scrollTop = newScrollHeight - oldScrollHeight;
        });
      }

      if (reset && records.length === 0 && !hasSentGreeting.value[targetUserId] && socketService.isConnected) {
        sendGreeting(targetUserId);
        hasSentGreeting.value[targetUserId] = true;
      }
    }
  } catch (error) {
    console.error('获取消息历史失败:', error);
  } finally {
    isLoadingMore.value = false;
    
    // 初始加载完成后强制滚动到底部，确保用户看到最新消息
    if (reset) {
      scrollToBottom(false);
    }
  }
};

// 滚动到底部按钮点击
const scrollToBottomBtnClick = () => {
  scrollToBottom(true);
};

// 滚动处理：上滑加载更早消息
const handleScroll = () => {
  if (!messageList.value || !initialLoadCompleted.value) return;

  const { scrollTop, clientHeight, scrollHeight } = messageList.value;
  showScrollToBottomBtn.value = scrollTop + clientHeight < scrollHeight - 300;

  // 上滑到顶部 → 加载更早消息
  if (scrollTop < 50 && hasMore.value && !isLoadingMore.value) {
    fetchMessageHistory(currentUserId.value, activeConversation.value?.targetUserId, false);
  }
};

// 发送消息
const sendMessage = async () => {
  if ((!inputContent.value.trim() && !selectedFile.value) || !activeConversation.value) return;

  if (!socketService.isConnected) {
    ElMessage.warning('WebSocket未连接，请稍后再试');
    return;
  }

  if (selectedFile.value) {
    const fileData = await uploadFile(selectedFile.value);
    if (!fileData) {
      ElMessage.error('文件上传失败，请重试');
      return;
    }

    const message = {
      senderId: currentUserId.value,
      receiverId: activeConversation.value.targetUserId,
      content: fileData.accessUrl,
      type: isImageFile(selectedFile.value) ? 2 : 4,
      fileName: selectedFile.value.name,
      fileSize: formatFileSize(selectedFile.value.size),
      sendTime: new Date().toISOString()
    };

    socketService.send(message)
      .then(() => {
        messageHistory.value.push(message);
        clearSelectedFile();
        scrollToBottom();
        fetchConversations();
      })
      .catch(error => {
        console.error('发送文件消息失败:', error);
        ElMessage.error('发送消息失败: ' + error.message);
      });
  } else {
    const message = {
      senderId: currentUserId.value,
      receiverId: activeConversation.value.targetUserId,
      content: inputContent.value.trim(),
      type: 1,
      sendTime: new Date().toISOString()
    };

    socketService.send(message)
      .then(() => {
        messageHistory.value.push(message);
        inputContent.value = '';
        scrollToBottom();
        fetchConversations();
      })
      .catch(error => {
        console.error('发送文本消息失败:', error);
        ElMessage.error('发送消息失败: ' + error.message);
      });
  }
};

// Shift+Enter换行
const handleShiftEnter = () => {
  inputContent.value += '\n';
};

// 选择会话
const selectConversation = (conv) => {
  activeConversationId.value = conv.id;
  activeConversation.value = conv;
  fetchMessageHistory(currentUserId.value, conv.targetUserId, true);

  if (isMobile.value) {
    showConversation.value = false;
    // 推入历史状态，拦截浏览器返回手势，展示会话列表而非离开页面
    window.history.pushState(null, '');
  }

  markAsRead(conv.userId || currentUserId.value, conv.targetUserId);
};

// 切换会话列表
const toggleConversation = () => {
  showConversation.value = !showConversation.value;
};

// 标记已读
const markAsRead = async (userId, targetUserId) => {
  try {
    await axios.post('/message/api/v1/messages/operation', {
      userId,
      targetUserId,
      operationType: 1
    });
    const conv = conversations.value.find(c => c.id === activeConversationId.value);
    if (conv) conv.unreadCount = 0;
  } catch (error) {
    console.error('标记已读失败:', error);
  }
};

// 格式化时间
const formatTime = (time) => {
  if (!time) return '';
  const date = new Date(time);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (isToday) {
    return timeStr;  // 今天只显示时间
  } else if (isYesterday) {
    return '昨天 ' + timeStr;
  } else {
    // 显示日期+时间
    return date.toLocaleDateString([], { month: '2-digit', day: '2-digit' }) + ' ' + timeStr;
  }
};

// 滚动到底部
const scrollToBottom = (smooth = true) => {
  const tryScroll = () => {
    if (messageList.value) {
      messageList.value.scrollTo({
        top: messageList.value.scrollHeight,
        behavior: smooth ? 'smooth' : 'instant'
      });
      showScrollToBottomBtn.value = false;
    }
  };
  // 首次打开用instant确保定位到底部，新消息用smooth
  nextTick(tryScroll);
  if (!smooth) {
    // 非平滑模式下重试一次（确保Vue渲染完成后执行）
    setTimeout(tryScroll, 100);
  }
};

// 监听窗口变化
watch(isMobile, (newVal) => {
  if (newVal) {
    showConversation.value = !!activeConversationId.value;
  } else {
    showConversation.value = true;
  }
});

// AI帮聊完整逻辑
const aiPopoverVisible = ref(false)
const aiReplyList = ref([])
const aiLoading = ref(false)
const aiError = ref('')

watch(aiPopoverVisible, (val) => {
  if (val) {
    getAiReplyList()
  } else {
    aiReplyList.value = []
    aiError.value = ''
  }
})

const getAiReplyList = async () => {
  if (!activeConversation.value) {
    ElMessage.warning('请先选择会话')
    aiPopoverVisible.value = false
    return
  }

  const targetUserId = activeConversation.value.targetUserId
  const userId = currentUserId.value
  aiLoading.value = true
  aiError.value = ''

  try {
      const res = await axios.get('/deepseek/api/deepseek/suggest', {
      params: {
  userId: userId,
  targetUserId: targetUserId
      }
    });

   const data = res.data;

    if (data.code === "200" && data.success && Array.isArray(data.data)) {
      aiReplyList.value = data.data.map(item =>
        item.replace(/^\d+\.\s*“?/g, '').replace(/”$/g, '')
      )
    } else {
      aiError.value = data.msg || 'AI回复获取失败'
    }
  } catch (err) {
    console.error('AI接口调用失败：', err)
    aiError.value = '网络异常，请检查接口是否启动'
  } finally {
    aiLoading.value = false
  }
}

const sendAiMessage = (text) => {
  if (!text) return
  inputContent.value = text
  sendMessage()
  aiPopoverVisible.value = false
}

// ========== 游戏邀请相关 ==========

const parseGameInvite = (msg) => {
  try {
    return JSON.parse(msg.content)
  } catch {
    return { gameTypeName: '游戏', inviterName: '好友', roomId: null, roomCode: null }
  }
}

const isGameInviteExpired = (msg) => {
  const now = Date.now()
  const sendTime = new Date(msg.sendTime).getTime()
  return (now - sendTime) > 120000 // 2分钟
}

const handleGameInviteClick = async (msg) => {
  // 只有接收方可以点击
  if (msg.senderId === currentUserId.value) return
  if (isGameInviteExpired(msg)) {
    ElMessage.info('邀请已过期')
    return
  }

  const invite = parseGameInvite(msg)
  if (!invite.roomCode) {
    ElMessage.error('邀请信息无效')
    return
  }

  try {
    const res = await gameApi.joinRoom(currentUserId.value, invite.roomCode)
    if (res.data.success) {
      ElMessage.success('已加入游戏房间')
      router.push(`/game/room/${invite.roomId}`)
    } else {
      ElMessage.error(res.data.msg || '加入房间失败，房间可能已关闭')
    }
  } catch (error) {
    console.error('加入游戏房间失败:', error)
    ElMessage.error('加入游戏房间失败')
  }
}
</script>

<style scoped>
.chat-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
  overflow: hidden;
}

.chat-wrapper {
  display: flex;
  flex: 1;
  overflow: hidden;
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-lg);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  margin-top: 16px;
  position: relative;
  min-height: 0;
  height: calc(100vh - 220px);
}

/* ── 会话列表 ── */
.conversation-list {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--app-border-light);
  background: var(--app-bg-soft);
  transition: all 0.3s ease;
}

.conversation-list.collapsed {
  width: 0;
  border-right: none;
  overflow: hidden;
}

.search-bar {
  padding: 14px 16px;
  border-bottom: 1px solid var(--app-border-light);
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: var(--app-text-muted);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 14px 10px 36px;
  border: none;
  border-radius: var(--app-radius-full);
  background: var(--app-bg-soft);
  font-size: 0.875rem;
  color: var(--app-text-primary);
  outline: none;
  transition: box-shadow 0.2s;
}

.search-input:focus {
  box-shadow: 0 0 0 2px var(--app-color-primary-soft);
}

.search-input::placeholder {
  color: var(--app-text-muted);
}

.conversations {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.conversations::-webkit-scrollbar {
  width: 4px;
}

.conversations::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 2px;
}

.conversations:hover::-webkit-scrollbar-thumb {
  background: var(--app-border-light);
}

.conversation-item {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 2px 8px;
  border-radius: var(--app-radius-md);
}

.conversation-item:hover {
  background: var(--app-color-primary-muted);
}

.conversation-item.active {
  background: var(--app-color-primary-soft);
}

.conv-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
  margin-right: 12px;
}

.conv-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid transparent;
  transition: border-color 0.2s;
}

.conversation-item.active .conv-avatar {
  border-color: var(--app-color-primary-light);
}

.online-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #2ecc71;
  border: 2px solid var(--app-bg-soft);
}

.online-dot.large {
  width: 14px;
  height: 14px;
}

.conv-info {
  flex: 1;
  min-width: 0;
}

.conv-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.nickname {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--app-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
  display: inline-flex;
  align-items: center;
}

.friend-tag {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  flex-shrink: 0;
}

.time {
  font-size: 0.75rem;
  color: var(--app-text-muted);
  flex-shrink: 0;
  margin-left: 8px;
}

.last-message {
  display: flex;
  align-items: center;
  gap: 6px;
}

.last-message span:first-child {
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.unread-badge {
  background: var(--app-color-primary);
  color: white;
  font-size: 0.6875rem;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  flex-shrink: 0;
}

/* 空会话 */
.empty-conversations {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--app-text-muted);
  gap: 12px;
}

.empty-conversations p {
  font-size: 0.875rem;
  margin: 0;
}

/* ── 聊天详情 ── */
.chat-detail {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  position: relative;
  background: var(--app-bg-elevated);
  height: 100%;
}

/* 聊天头部 */
.chat-header {
  padding: 12px 20px;
  border-bottom: 1px solid var(--app-border-light);
  display: flex;
  align-items: center;
  background: var(--app-bg-elevated);
  flex-shrink: 0;
}

.back-btn {
  background: none;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-secondary);
  transition: all 0.2s;
  margin-right: 8px;
}

.back-btn:hover {
  background: var(--app-bg-soft);
  color: var(--app-color-primary);
}

.chat-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-header-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.chat-header-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
}

.chat-header-text {
  display: flex;
  flex-direction: column;
}

.target-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
}

.target-status {
  font-size: 0.75rem;
  color: #2ecc71;
}

/* 添加好友按钮 */
.add-friend-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1px solid var(--app-color-primary-light);
  border-radius: var(--app-radius-full);
  background: transparent;
  color: var(--app-color-primary);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  white-space: nowrap;
}
.add-friend-btn:hover {
  background: var(--app-color-primary);
  color: white;
  box-shadow: 0 2px 8px rgba(255, 126, 126, 0.3);
}

/* 通话按钮组 */
.call-btns {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
}

.call-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid var(--app-border-light);
  background: transparent;
  color: var(--app-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}
.call-btn:hover {
  background: var(--app-color-primary);
  color: white;
  border-color: var(--app-color-primary);
  box-shadow: 0 2px 8px rgba(255, 126, 126, 0.3);
}

/* ── 消息列表 ── */
.message-list {
  flex: 1;
  padding: 16px 20px;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: var(--app-bg-soft);
  scroll-behavior: smooth;
  max-height: calc(100vh - 200px);
}

.message-list::-webkit-scrollbar {
  width: 8px;
}

.message-list::-webkit-scrollbar-track {
  background: var(--app-bg-soft);
}

.message-list::-webkit-scrollbar-thumb {
  background: var(--app-border-light);
  border-radius: 4px;
}

.message-list::-webkit-scrollbar-thumb:hover {
  background: var(--app-border-subtle);
}

.loading-more {
  text-align: center;
  padding: 12px 0;
  color: var(--app-text-muted);
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--app-border-light);
  border-top-color: var(--app-color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.scroll-to-bottom-btn {
  position: absolute;
  bottom: 80px;
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--app-bg-elevated);
  color: var(--app-color-primary);
  border: 1px solid var(--app-border-light);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--app-shadow-card);
  transition: all 0.2s;
  z-index: 5;
}

.scroll-to-bottom-btn:hover {
  background: var(--app-color-primary);
  color: white;
  border-color: var(--app-color-primary);
  transform: translateY(-2px);
  box-shadow: var(--app-shadow-card-hover);
}

/* ── 消息项 ── */
.message-item {
  margin-bottom: 16px;
  display: flex;
  max-width: 80%;
  animation: msgFadeIn 0.25s ease;
}

@keyframes msgFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.message-item.sent {
  margin-left: auto;
  flex-direction: row-reverse;
}

.msg-avatar-wrapper {
  flex-shrink: 0;
}

.msg-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
}

.message-item.received .msg-avatar-wrapper {
  margin-right: 10px;
}

.message-item.sent .msg-avatar-wrapper {
  margin-left: 10px;
}

.msg-content {
  max-width: calc(100% - 44px);
}

.msg-text {
  padding: 10px 16px;
  border-radius: 18px;
  word-break: break-word;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.message-item.received .msg-text {
  background: var(--app-bg-elevated);
  color: var(--app-text-primary);
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}

.message-item.sent .msg-text {
  background: var(--app-gradient-primary);
  color: white;
  border-bottom-right-radius: 4px;
}

.msg-time {
  font-size: 0.6875rem;
  color: var(--app-text-muted);
  margin-top: 4px;
}

.message-item.received .msg-time {
  text-align: left;
  padding-left: 4px;
}

.message-item.sent .msg-time {
  text-align: right;
  padding-right: 4px;
}

/* ── 图片消息 ── */
.msg-image img {
  max-width: 220px;
  max-height: 220px;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.msg-image img:hover {
  transform: scale(1.02);
}

/* ── 文件消息 ── */
.msg-file .file-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: var(--app-bg-elevated);
  border-radius: 12px;
  color: var(--app-text-primary);
  text-decoration: none;
  max-width: 260px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  transition: all 0.2s;
  border: 1px solid var(--app-border-subtle);
}

.msg-file .file-link:hover {
  background: var(--app-bg-soft);
  border-color: var(--app-color-primary-muted);
}

.gift-link .file-icon {
  color: var(--app-color-primary);
}

.file-icon {
  flex-shrink: 0;
}

.file-name {
  font-size: 0.8125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

/* ── 礼物请求消息 ── */
.msg-gift-request {
  padding: 12px 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #FFF5F5 0%, #FFF8F0 100%);
  border: 1px solid rgba(255, 126, 126, 0.2);
  word-break: break-word;
  transition: all 0.2s ease;
}

.msg-gift-request.clickable {
  cursor: pointer;
}

.msg-gift-request.clickable:hover {
  border-color: var(--app-color-primary);
  box-shadow: 0 2px 8px rgba(255, 126, 126, 0.15);
  transform: translateY(-1px);
}

.gift-request-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--app-text-primary);
}

.gift-request-status {
  margin-top: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
}

.accepted {
  color: #2ecc71;
}

.rejected {
  color: #e74c3c;
}

/* ── 游戏邀请消息 ── */
.msg-game-invite {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #F0E6FF 0%, #E8F4FD 100%);
  border: 1px solid rgba(108, 92, 231, 0.15);
  transition: all 0.2s ease;
  max-width: 320px;
}

.msg-game-invite.clickable {
  cursor: pointer;
}

.msg-game-invite.clickable:hover {
  border-color: #6C5CE7;
  box-shadow: 0 2px 12px rgba(108, 92, 231, 0.15);
  transform: translateY(-1px);
}

.msg-game-invite.expired {
  opacity: 0.5;
  background: var(--app-bg-soft);
}

.game-invite-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(108, 92, 231, 0.1);
  color: #6C5CE7;
  flex-shrink: 0;
}

.game-invite-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.game-invite-title {
  font-size: 0.875rem;
  color: var(--app-text-primary);
  line-height: 1.5;
}

.game-invite-title strong {
  color: #6C5CE7;
}

.game-invite-status {
  font-size: 0.8125rem;
  color: #6C5CE7;
  font-weight: 500;
}

.msg-game-invite.expired .game-invite-status {
  color: var(--app-text-muted);
}

/* ── 输入区域 ── */
.input-area {
  padding: 12px 16px;
  border-top: 1px solid var(--app-border-light);
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: var(--app-bg-elevated);
  flex-shrink: 0;
}

.file-upload-btn {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  transition: all 0.2s;
  flex-shrink: 0;
}

.file-upload-btn:hover {
  background: var(--app-bg-soft);
  color: var(--app-color-primary);
}

.file-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-md);
  width: 100%;
  margin-bottom: 4px;
}

.image-preview {
  max-width: 60px;
  max-height: 60px;
  border-radius: 6px;
  object-fit: cover;
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 8px;
}

.file-size {
  color: var(--app-text-muted);
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* 语音按钮 */
.voice-btn {
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  transition: all 0.2s;
  flex-shrink: 0;
}
.voice-btn:hover {
  background: var(--app-bg-soft);
  color: var(--app-color-primary);
}

/* 录制指示器 */
.recording-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(231, 76, 60, 0.08);
  border-radius: var(--app-radius-full);
  flex-shrink: 0;
}
.recording-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e74c3c;
  animation: recording-pulse 1s ease-in-out infinite;
}
@keyframes recording-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
.recording-timer {
  font-size: 0.8125rem;
  color: #e74c3c;
  font-variant-numeric: tabular-nums;
  min-width: 30px;
}
.stop-recording-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: #e74c3c;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.stop-recording-btn:hover {
  transform: scale(1.1);
}

/* 语音消息 */
.msg-voice {
  cursor: pointer;
  user-select: none;
}
.voice-player {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: var(--app-radius-lg);
  background: var(--app-bg-soft);
  transition: all 0.2s;
  min-width: 120px;
}
.msg-voice:hover .voice-player {
  background: var(--app-color-primary-soft);
}
.received .voice-player {
  background: var(--app-bg-soft);
}
.sent .voice-player {
  background: rgba(255, 126, 126, 0.08);
}
.msg-voice:hover .voice-player {
  background: var(--app-color-primary-soft);
}
.voice-icon {
  flex-shrink: 0;
  color: var(--app-color-primary);
}
.voice-player.playing .voice-icon {
  color: var(--app-color-primary);
}
.voice-wave {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 20px;
}
.wave-bar {
  display: block;
  width: 3px;
  height: 8px;
  border-radius: 2px;
  background: var(--app-color-primary-light);
  opacity: 0.4;
  transition: all 0.2s;
}
.wave-bar.active {
  animation: voice-wave 0.5s ease-in-out infinite alternate;
  opacity: 1;
}
.wave-bar:nth-child(1) { animation-delay: 0s; height: 8px; }
.wave-bar:nth-child(2) { animation-delay: 0.15s; height: 14px; }
.wave-bar:nth-child(3) { animation-delay: 0.3s; height: 10px; }
.wave-bar:nth-child(4) { animation-delay: 0.45s; height: 16px; }
@keyframes voice-wave {
  0% { height: 6px; }
  100% { height: 18px; }
}
.voice-duration {
  font-size: 0.75rem;
  color: var(--app-text-muted);
  font-variant-numeric: tabular-nums;
  margin-left: auto;
}

.remove-file {
  background: none;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e74c3c;
  transition: all 0.2s;
  flex-shrink: 0;
}

.remove-file:hover {
  background: rgba(231, 76, 60, 0.1);
}

.message-input {
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 10px 16px;
  border: 1px solid var(--app-border-light);
  border-radius: 20px;
  resize: none;
  outline: none;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.5;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: var(--app-bg-soft);
}

.message-input:focus {
  border-color: var(--app-color-primary);
  box-shadow: 0 0 0 3px var(--app-color-primary-soft);
  background: var(--app-bg-elevated);
}

.message-input::placeholder {
  color: var(--app-text-muted);
}

.send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--app-gradient-primary);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.send-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 126, 126, 0.35);
}

.send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* ── AI帮聊 ── */
.ai-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--app-gradient-secondary);
  color: white;
  border: none;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.ai-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 14px rgba(108, 92, 231, 0.4);
}

.ai-reply-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.ai-reply-item {
  padding: 10px 14px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--app-text-primary);
  transition: all 0.2s;
  border: 1px solid transparent;
}

.ai-reply-item:hover {
  background: var(--app-color-primary-soft);
  border-color: rgba(255, 126, 126, 0.3);
  transform: translateX(2px);
}

.ai-loading {
  padding: 20px;
  text-align: center;
  color: var(--app-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.875rem;
}

.ai-loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--app-border-light);
  border-top-color: var(--app-color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.ai-error {
  padding: 16px;
  text-align: center;
  color: #f56c6c;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.875rem;
}

/* ── 响应式 ── */
@media (max-width: 768px) {
  .chat-page {
    padding: 0;
  }

  .chat-wrapper {
    border-radius: 0;
    border: none;
    margin-top: 0;
    height: 100%;
  }

  .conversation-list {
    position: absolute;
    z-index: 10;
    width: 100%;
    height: 100%;
    background: var(--app-bg-elevated);
  }

  .chat-detail {
    width: 100%;
  }

  .message-list {
    padding: 12px 14px;
    max-height: calc(100vh - 180px);
  }

  .message-item {
    max-width: 90%;
  }

  .scroll-to-bottom-btn {
    bottom: 70px;
    right: 12px;
    width: 36px;
    height: 36px;
  }

  .input-area {
    padding: 10px 12px;
  }
}
</style>